/**
 * coachTours.ts — re-exports the compiled guided-tour content plus the
 * lookups the engine needs (`tourForRoute`, `firstRouteFor`, `nudgesFor`).
 *
 * The actual tours and nudges are authored in Markdown under src/tours/*.md
 * and compiled by scripts/build-tours.js into src/tours/tours.generated.ts
 * (checked in, regenerated by `npm run tours:build` / the prestart+prebuild
 * hooks). This module is the one import path the rest of the app uses, so
 * that generated file can be regenerated — or its shape reworked — without
 * touching a single call site.
 *
 * Anchors still resolve via `document.querySelector('[data-coach="…"]')`
 * rather than an imperative ref registry — carried over from v1: the mode
 * switcher renders from two mutually exclusive branches in Sidebar.tsx, a
 * ref map can't tell which one won a given commit, and a string literal is
 * greppable/pinnable the same way `sidebarModeSwitcher-wiring.test.ts` pins
 * the rest of that component after PR #52 silently rewound it.
 * `COACH_ANCHOR_IDS` is the generated source of truth the `CoachAnchorId`
 * union is derived from, so a wiring test can verify every id is actually
 * written to the DOM somewhere.
 */

import { TOURS, NUDGES, COACH_ANCHOR_IDS, TOUR_ROUTES } from '../tours/tours.generated';
import type { GeneratedTourId } from '../tours/tours.generated';
import type { CoachNudge } from './coachTypes';
import { tourIdForPath } from './coachRoutes';

export { TOURS as COACH_TOURS, NUDGES as COACH_NUDGES, COACH_ANCHOR_IDS, TOUR_ROUTES };
export type { CoachStep, CoachTour, CoachNudge, CoachAdvance, CoachPlacementSide } from './coachTypes';

export type CoachAnchorId = typeof COACH_ANCHOR_IDS[number];
export type CoachTourId = GeneratedTourId;

/** Which tour, if any, owns the page at `pathname` — see TOUR_ROUTES' order. */
export function tourForRoute(pathname: string): CoachTourId | null {
  return tourIdForPath(pathname, TOUR_ROUTES) as CoachTourId | null;
}

/**
 * The first route this tour is reachable from without a specific record id,
 * for the "navigate, then start" hop (e.g. the account popover's Show me
 * around on a page with no tour of its own). Tours whose only route(s) carry
 * a `:param` (workspace, app-details) return null — there is nowhere generic
 * to send someone; starting them off-route is a no-op instead of a hop that
 * could never land.
 */
export function firstRouteFor(tourId: CoachTourId): string | null {
  const tour = TOURS[tourId];
  if (!tour) return null;
  return tour.routes.find((r) => !r.includes(':')) ?? null;
}

/** Nudges registered for a given event or milestone name. */
export function nudgesFor(eventName: string): CoachNudge[] {
  return Object.values(NUDGES).filter((n) => n.when === eventName);
}
